/* Iter 418.ap (Dirk, 2026-02-28) — Plant Report embed for the
   Customer > Performance tab. Same components (date range, KPIs,
   trend charts, AI chat, parameter table, PDF button) but hard-
   scoped to ONE customer_id so we don't need the picker or the
   route-level customers list.
*/
import { useEffect, useMemo, useState } from "react";
import { Download, Activity, AlertTriangle, TrendingUp, Clock, Users, Wrench, LifeBuoy, RefreshCw, CalendarDays, FileText, Sparkles } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import AIInsightsPanel from "./AIInsightsPanel";
import AIChatPanel from "./AIChatPanel";
import InteractiveTrendChart from "./InteractiveTrendChart";

const PRESETS = [
  { key: "today", label: "Today", days: 1 },
  { key: "3d",    label: "3 days", days: 3 },
  { key: "7d",    label: "7 days", days: 7 },
  { key: "30d",   label: "30 days", days: 30 },
  { key: "90d",   label: "90 days", days: 90 },
  { key: "365d",  label: "12 months", days: 365 },
  { key: "all",   label: "All time", days: 0 },
];

function todayIsoDate() { return new Date().toISOString().slice(0, 10); }
function daysAgoIso(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}
function fmtNum(v, digits = 0) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return Number(v).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
function fmtPct(v) { return (v === null || v === undefined || Number.isNaN(v)) ? "—" : `${Number(v).toFixed(1)}%`; }
function fmtHours(h) { if (h === null || h === undefined) return "—"; return h < 1 ? `${Math.round(h * 60)}m` : `${h.toFixed(1)}h`; }

export default function PlantReportEmbed({ customerId, customerName }) {
  const { user } = useAuth();
  const isCrm = (user?.role || "").toLowerCase() !== "client";
  const [preset, setPreset] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState(false);

  const { fromIso, toIso, allTime } = useMemo(() => {
    if (customFrom && customTo) {
      return {
        fromIso: new Date(`${customFrom}T00:00:00Z`).toISOString(),
        toIso: new Date(`${customTo}T23:59:59Z`).toISOString(),
        allTime: false,
      };
    }
    const p = PRESETS.find((x) => x.key === preset) || PRESETS[3];
    if (p.key === "all") return { fromIso: "", toIso: "", allTime: true };
    const from = new Date();
    from.setUTCDate(from.getUTCDate() - p.days);
    return { fromIso: from.toISOString(), toIso: new Date().toISOString(), allTime: false };
  }, [preset, customFrom, customTo]);

  const load = async () => {
    if (!customerId) return;
    setLoading(true);
    setErr("");
    try {
      const params = allTime ? { all_time: 1 } : { from: fromIso, to: toIso };
      const { data } = await api.get(`/perf/plant-report/${customerId}`, { params });
      setReport(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId, fromIso, toIso, allTime]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const params = allTime ? { all_time: 1 } : { from: fromIso, to: toIso };
      const res = await api.get(`/perf/plant-report/${customerId}/pdf`, { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (report?.customer_name || customerName || customerId).replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 64);
      a.download = `HidroVerse_PlantReport_${safe}.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch (e) { alert(formatErr(e) || "PDF failed."); }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-3 min-w-0" data-testid="plant-report-embed">
      {/* Range + actions */}
      <div className="hv-card p-3 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1">
            <CalendarDays className="w-3 h-3 text-[#647087]" />
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => { setPreset(p.key); setCustomFrom(""); setCustomTo(""); }}
                className={`hv-btn hv-btn-ghost text-xs ${!customFrom && !customTo && preset === p.key ? "text-emerald-300 border-emerald-500/50" : ""}`}
                data-testid={`plant-report-embed-preset-${p.key}`}
              >{p.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="hv-btn hv-btn-ghost text-xs"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              type="button"
              onClick={downloadPdf}
              disabled={downloading || loading || !report}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="plant-report-embed-download"
            >
              <Download className={`w-3.5 h-3.5 ${downloading ? "animate-pulse" : ""}`} />
              {downloading ? "Preparing…" : "Download PDF"}
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-[#647087]">Custom range:</span>
          <input
            type="date"
            value={customFrom || daysAgoIso(30)}
            max={customTo || todayIsoDate()}
            onChange={(e) => { setCustomFrom(e.target.value); if (!customTo) setCustomTo(todayIsoDate()); }}
            className="hv-input text-xs"
          />
          <span className="text-[#647087]">→</span>
          <input
            type="date"
            value={customTo || todayIsoDate()}
            min={customFrom || undefined}
            max={todayIsoDate()}
            onChange={(e) => { setCustomTo(e.target.value); if (!customFrom) setCustomFrom(daysAgoIso(30)); }}
            className="hv-input text-xs"
          />
          {(customFrom || customTo) && (
            <button type="button" onClick={() => { setCustomFrom(""); setCustomTo(""); }} className="hv-btn hv-btn-ghost text-[10px]">Reset</button>
          )}
        </div>
      </div>

      {err && <div className="hv-card border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">{err}</div>}
      {loading && !report && <div className="hv-card p-6 text-center text-sm text-[#647087]">Loading plant report…</div>}

      {report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Kpi icon={FileText} label="Total readings" value={fmtNum(report.readings?.total)} accent="#22D3EE" />
            <Kpi icon={Activity} label="Green" value={`${fmtNum(report.readings?.green)} · ${fmtPct(report.readings?.green_pct)}`} accent="#10B981" />
            <Kpi icon={AlertTriangle} label="Warnings" value={`${fmtNum(report.readings?.warn)} · ${fmtPct(report.readings?.warn_pct)}`} accent="#F59E0B" />
            <Kpi icon={AlertTriangle} label="Errors" value={`${fmtNum(report.readings?.alarm)} · ${fmtPct(report.readings?.alarm_pct)}`} accent="#F43F5E" />
            <Kpi icon={TrendingUp} label="Uptime" value={fmtPct(report.uptime?.uptime_pct)} accent="#10B981" />
            <Kpi icon={Clock} label="Downtime" value={fmtHours(report.uptime?.downtime_hours)} accent="#F43F5E" />
            <Kpi icon={Wrench} label="Job cards" value={fmtNum(report.tickets?.jobcards_count)} accent="#F59E0B" />
            <Kpi icon={LifeBuoy} label="Tickets" value={fmtNum(report.tickets?.tickets_count)} accent="#F43F5E" />
          </div>

          {report.charts?.length > 0 && (
            <div className="hv-card p-3" data-testid="plant-report-embed-charts">
              <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold mb-2 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Trends ({report.charts.length})
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {report.charts.map((c, i) => (
                  <div key={i} className="border border-white/5 rounded-md overflow-hidden bg-slate-900/60">
                    <div className="px-2 pt-2 pb-1">
                      <div className="text-[11px] font-semibold">{c.name}{c.unit ? <span className="text-[#647087]"> ({c.unit})</span> : null}</div>
                      <div className="text-[9px] text-[#647087]">{c.equipment_name} · {fmtNum(c.count)} readings</div>
                    </div>
                    <InteractiveTrendChart chart={c} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {isCrm && (
            <>
              <AIChatPanel customerId={customerId} customerName={report?.customer_name || customerName} fromIso={fromIso} toIso={toIso} allTime={allTime} />
              <AIInsightsPanel customerId={customerId} fromIso={fromIso} toIso={toIso} allTime={allTime} customerName={report?.customer_name || customerName} />
            </>
          )}

          {report.parameters?.length > 0 && (
            <div className="hv-card p-3" data-testid="plant-report-embed-params">
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-2 flex items-center gap-1">
                <Activity className="w-3 h-3 text-emerald-400" /> Plant metrics ({report.parameters.length})
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-[9px] uppercase tracking-wider text-[#647087]">
                      <th className="text-left py-1.5 px-2">Parameter</th>
                      <th className="text-right py-1.5 px-2">Readings</th>
                      <th className="text-right py-1.5 px-2">In target</th>
                      <th className="text-right py-1.5 px-2">Trend</th>
                      <th className="text-right py-1.5 px-2">Avg</th>
                      <th className="text-right py-1.5 px-2">Range</th>
                      <th className="text-right py-1.5 px-2">Δ (totalisers)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {report.parameters.map((p, i) => {
                      const inC = p.in_target_pct >= 90 ? "text-emerald-300" : p.in_target_pct >= 70 ? "text-amber-300" : "text-rose-300";
                      const trM = { up: { s: "▲", c: "text-cyan-300" }, down: { s: "▼", c: "text-amber-300" }, flat: { s: "→", c: "text-[#647087]" } };
                      const tr = trM[p.trend] || trM.flat;
                      return (
                        <tr key={i}>
                          <td className="py-1.5 px-2 text-white truncate max-w-[220px]">
                            {p.name}{p.unit && <span className="text-[#647087]"> ({p.unit})</span>}
                            <div className="text-[9px] text-[#647087]">{p.equipment_name}</div>
                          </td>
                          <td className="py-1.5 px-2 text-right font-mono">{fmtNum(p.count)}</td>
                          <td className={`py-1.5 px-2 text-right font-mono ${inC}`}>{fmtPct(p.in_target_pct)}</td>
                          <td className={`py-1.5 px-2 text-right font-mono ${tr.c}`}>{tr.s}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{fmtNum(p.avg, 2)}</td>
                          <td className="py-1.5 px-2 text-right font-mono text-[#a0aabf]">{fmtNum(p.min, 2)}–{fmtNum(p.max, 2)}</td>
                          <td className="py-1.5 px-2 text-right font-mono">
                            {p.totalizer && p.delta !== null && p.delta !== undefined
                              ? <span className="text-emerald-300">{fmtNum(p.delta, 2)}</span>
                              : <span className="text-[#3d475a]">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(!report.parameters || report.parameters.length === 0) && (
            <div className="hv-card p-4 text-sm text-[#647087] italic text-center">
              No parameter readings captured for this window.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="hv-card p-2.5 flex items-center gap-2">
      <Icon className="w-4 h-4 shrink-0" style={{ color: accent }} />
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-wider text-[#647087]">{label}</div>
        <div className="text-sm font-bold truncate" style={{ color: accent }}>{value}</div>
      </div>
    </div>
  );
}
